import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { CalendarDays, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import DeliveryCard from "./DeliveryCard";
import DeliveryFormFields from "./DeliveryFormFields";
import { todayBR, addDays, fmtYmd, weekdayBR, deliveryWhatsAppUrl } from "./deliveryUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGES = { hoje: "Hoje", semana: "Próximos 7 dias", mes: "Próximos 30 dias", atrasadas: "Atrasadas", periodo: "Período" };

const Filters = ({ f, setF, drivers, isManager }) => (
  <div className="flex flex-wrap items-end gap-2 border rounded-lg p-3 bg-slate-50">
    <div className="flex gap-1 flex-wrap">
      {Object.entries(RANGES).map(([k, v]) => (
        <Button key={k} size="sm" variant={f.range === k ? "default" : "outline"} onClick={() => setF({ ...f, range: k })} data-testid={`delivery-range-${k}`}>{v}</Button>
      ))}
    </div>
    {f.range === "periodo" && (
      <>
        <Input type="date" className="w-40" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="delivery-filter-start" />
        <Input type="date" className="w-40" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="delivery-filter-end" />
      </>
    )}
    {f.range !== "atrasadas" && (
      <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
        <SelectTrigger className="w-36" data-testid="delivery-filter-status"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos status</SelectItem>
          <SelectItem value="agendada">Agendadas</SelectItem>
          <SelectItem value="em_rota">Em rota</SelectItem>
          <SelectItem value="entregue">Entregues</SelectItem>
          <SelectItem value="cancelada">Canceladas</SelectItem>
        </SelectContent>
      </Select>
    )}
    {isManager && (
      <Select value={f.driver} onValueChange={(v) => setF({ ...f, driver: v })}>
        <SelectTrigger className="w-48" data-testid="delivery-filter-driver"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos transportadores</SelectItem>
          <SelectItem value="none">Sem transportador</SelectItem>
          {drivers.map((d) => <SelectItem key={d.user_id} value={d.user_id}>{d.name}</SelectItem>)}
        </SelectContent>
      </Select>
    )}
  </div>
);

const buildQuery = (f) => {
  const t = todayBR();
  const p = new URLSearchParams();
  if (f.range === "atrasadas") p.set("status", "atrasadas");
  else {
    const [s, e] = f.range === "hoje" ? [t, t] : f.range === "semana" ? [t, addDays(t, 6)] : f.range === "mes" ? [t, addDays(t, 29)] : [f.start, f.end];
    if (s) p.set("start", s);
    if (e) p.set("end", e);
    if (f.status !== "all") p.set("status", f.status);
  }
  if (f.driver !== "all") p.set("driver_user_id", f.driver);
  return p.toString();
};

export default function DeliveriesTab({ companyId, companyName }) {
  const [f, setF] = useState({ range: "semana", start: todayBR(), end: addDays(todayBR(), 6), status: "all", driver: "all" });
  const [list, setList] = useState([]);
  const [role, setRole] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [confirmDlg, setConfirmDlg] = useState(null);
  const [editDlg, setEditDlg] = useState(null);
  const isManager = role === "manager";

  const load = () => axios.get(`${API}/companies/${companyId}/factory/deliveries?${buildQuery(f)}`)
    .then((r) => { setList(r.data.deliveries || []); setRole(r.data.role); })
    .catch((e) => toast.error(e.response?.data?.detail || "Erro ao carregar entregas"));
  useEffect(() => { if (companyId) load(); }, [companyId, f]);
  useEffect(() => { if (isManager) axios.get(`${API}/companies/${companyId}/factory/delivery-drivers`).then((r) => setDrivers(r.data || [])); }, [isManager, companyId]);

  const act = async (d, path, body, msg) => {
    try { await axios.post(`${API}/companies/${companyId}/factory/deliveries/${d.id}/${path}`, body); toast.success(msg); load(); return true; }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); return false; }
  };
  const on = {
    start: (d) => act(d, "start", {}, "Saiu para entrega"),
    confirm: (d) => setConfirmDlg({ d, received_by: "", notes: "" }),
    edit: (d) => setEditDlg({ d, v: { scheduled_date: d.scheduled_date, address: d.address, contact_phone: d.contact_phone, driver_user_id: d.driver_user_id, notes: d.notes } }),
    cancel: (d) => window.confirm(`Cancelar a entrega do pedido #${d.sale_number}?`) && act(d, "cancel", {}, "Entrega cancelada"),
    reopen: (d) => act(d, "reopen", {}, "Entrega reaberta"),
  };

  const submitConfirm = async () => {
    if (!confirmDlg.received_by.trim()) { toast.error("Informe quem recebeu"); return; }
    const ok = await act(confirmDlg.d, "confirm", { received_by: confirmDlg.received_by, notes: confirmDlg.notes || null }, "Entrega confirmada");
    if (ok) setConfirmDlg({ ...confirmDlg, done: { ...confirmDlg.d, status: "entregue", received_by: confirmDlg.received_by, delivered_at: new Date().toISOString() } });
  };
  const submitEdit = async () => {
    try { await axios.put(`${API}/companies/${companyId}/factory/deliveries/${editDlg.d.id}`, editDlg.v); toast.success("Entrega reagendada"); setEditDlg(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const today = todayBR();
  const groups = list.reduce((acc, d) => { (acc[d.scheduled_date] = acc[d.scheduled_date] || []).push(d); return acc; }, {});

  return (
    <div className="space-y-3" data-testid="deliveries-tab">
      {role === "driver" && <div className="text-sm bg-amber-50 border border-amber-300 rounded p-2">Você está vendo apenas as entregas atribuídas a você.</div>}
      <Filters f={f} setF={setF} drivers={drivers} isManager={isManager} />
      {Object.keys(groups).sort().map((day) => (
        <div key={day} data-testid={`delivery-day-${day}`}>
          <h4 className="font-bold text-sm flex items-center gap-1 mb-2 capitalize">
            <CalendarDays className="h-4 w-4" />{fmtYmd(day)} · {weekdayBR(day)}{day === today ? " (hoje)" : ""} · {groups[day].length} entrega(s)
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {groups[day].map((d) => <DeliveryCard key={d.id} d={d} isManager={isManager} companyName={companyName} on={on} late={day < today && ["agendada", "em_rota"].includes(d.status)} />)}
          </div>
        </div>
      ))}
      {list.length === 0 && <div className="border rounded p-8 text-center text-sm" data-testid="deliveries-empty">Nenhuma entrega neste período. Marque "Com entrega" ao vender no Frente de Caixa.</div>}

      <Dialog open={!!confirmDlg} onOpenChange={(o) => !o && setConfirmDlg(null)}>
        <DialogContent className="max-w-md" data-testid="delivery-confirm-dialog">
          <DialogHeader><DialogTitle>Confirmar entrega #{String(confirmDlg?.d.sale_number || "").padStart(4, "0")}</DialogTitle></DialogHeader>
          {confirmDlg && !confirmDlg.done && (
            <div className="space-y-2">
              <div>
                <label className="text-xs font-bold">Nome de quem recebeu *</label>
                <Input value={confirmDlg.received_by} onChange={(e) => setConfirmDlg({ ...confirmDlg, received_by: e.target.value })} data-testid="delivery-received-by" autoFocus />
              </div>
              <div>
                <label className="text-xs font-bold">Observação</label>
                <Input value={confirmDlg.notes} onChange={(e) => setConfirmDlg({ ...confirmDlg, notes: e.target.value })} data-testid="delivery-confirm-notes" />
              </div>
            </div>
          )}
          {confirmDlg?.done && <p className="text-sm">Entrega registrada. Deseja avisar o cliente pelo WhatsApp?</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDlg(null)}>{confirmDlg?.done ? "Fechar" : "Cancelar"}</Button>
            {confirmDlg?.done
              ? <Button onClick={() => { window.open(deliveryWhatsAppUrl(confirmDlg.done, companyName, "entregue"), "_blank"); setConfirmDlg(null); }} data-testid="delivery-confirm-whatsapp"><MessageCircle className="h-4 w-4 mr-1" />Avisar cliente</Button>
              : <Button onClick={submitConfirm} data-testid="delivery-confirm-submit">Confirmar entrega</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editDlg} onOpenChange={(o) => !o && setEditDlg(null)}>
        <DialogContent className="max-w-lg" data-testid="delivery-edit-dialog">
          <DialogHeader><DialogTitle>Reagendar entrega #{String(editDlg?.d.sale_number || "").padStart(4, "0")}</DialogTitle></DialogHeader>
          {editDlg && <DeliveryFormFields value={editDlg.v} onChange={(v) => setEditDlg({ ...editDlg, v })} drivers={drivers} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDlg(null)}>Cancelar</Button>
            <Button onClick={submitEdit} data-testid="delivery-edit-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
